import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Facebook, Linkedin, Mail, MessageCircle, Send, Phone, Share2, ShieldCheck, ExternalLink } from "lucide-react";
import { useFetch } from "../lib/hooks";
import { SITE_URL, fmtDateTime, imgSrc } from "../lib/api";
import { ARTICLE_TYPES, INFO_CHECK } from "../lib/brand";
import { Seo } from "../components/site/Seo";
import { BrandLoader } from "../components/site/Logo";
import { ArticleCard, DemoBadge } from "../components/site/ArticleCard";
import { SectionHeading, AdSlot } from "../components/site/Blocks";
import NotFound from "./NotFound";
import { api } from "../lib/api";
import { useReader } from "../context/ReaderContext";
import { WhatItMeans } from "./reader/WhatItMeans";
import { SaveButton } from "./reader/MojeVijesti";
import { track } from "../lib/analytics";
import { useArticleEngagement } from "../lib/engagement";

const SOURCE_LABELS = { official: "Službeni izvor", media: "Medij", study: "Studija / statistika", statement: "Izjava", other: "Ostalo" };

const Share = ({ url, title, id }) => {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent(url);
  const t = encodeURIComponent(title);
  const links = [
    ["facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${enc}`],
    ["whatsapp", MessageCircle, `https://wa.me/?text=${t}%20${enc}`],
    ["viber", Phone, `viber://forward?text=${t}%20${enc}`],
    ["telegram", Send, `https://t.me/share/url?url=${enc}&text=${t}`],
    ["linkedin", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${enc}`],
    ["email", Mail, `mailto:?subject=${t}&body=${enc}`],
  ];
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    track("share_click", { method: "copy" }, id);
    setCopied(true);
    toast.success("Link članka je kopiran");
    setTimeout(() => setCopied(false), 2000);
  };
  const native = async () => {
    try {
      await navigator.share({ title, url });
      track("share_click", { method: "native" }, id);
    } catch {
      /* cancelled by user: not counted */
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-links">
      <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Podijeli</span>
      {links.map(([k, Icon, href]) => (
        <a key={k} href={href} target="_blank" rel="noopener noreferrer" onClick={() => track("share_click", { method: k }, id)} data-bp-share aria-label={`Podijeli: ${k}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-pulse hover:text-pulse" data-testid={`share-${k}`}>
          <Icon className="h-4 w-4" />
        </a>
      ))}
      {typeof navigator !== "undefined" && navigator.share && (
        <button onClick={native} aria-label="Podijeli putem uređaja" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border transition-colors hover:border-pulse hover:text-pulse" data-testid="share-native"><Share2 className="h-4 w-4" /></button>
      )}
      <button onClick={copy} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-3 text-xs font-semibold transition-colors hover:border-pulse hover:text-pulse" data-testid="copy-article-url-button">
        <Copy className="h-3.5 w-3.5" /> {copied ? "Kopirano" : "Kopiraj link"}
      </button>
    </div>
  );
};

const CLAIM = { verified: "Tačno", partially_verified: "Djelimično tačno", misleading: "Obmanjujuće", false: "Netačno", unverifiable: "Nije moguće provjeriti" };

const InfoCheck = ({ ic }) => {
  if (!ic || !ic.status || ic.status === "not_checked") return null;
  const s = INFO_CHECK[ic.status];
  return (
    <aside className="my-10 border border-border bg-card p-6" aria-labelledby="ic-h" data-testid="balkan-info-check-box">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="ic-h" className="flex items-center gap-2 font-display text-xl font-semibold text-ink"><ShieldCheck className="h-5 w-5 text-brandblue dark:text-sky-300" /> Balkan Info Check</h2>
        <span className={`rounded-sm px-2 py-1 text-xs font-bold ${s.tone}`} data-testid="info-check-status">{s.label}</span>
      </div>
      {ic?.summary && <p className="mt-3 text-sm leading-relaxed">{ic.summary}</p>}
      {ic?.checked_at && <p className="mt-3 text-xs text-muted-foreground">Provjerio/la: {ic.checked_by_name || "Redakcija"} · {fmtDateTime(ic.checked_at)}</p>}
      {ic.claims?.length > 0 && <ul className="mt-4 space-y-2 text-sm" data-testid="info-check-claims">{ic.claims.map((c, i) => <li key={i} className="border-l-2 border-border pl-3"><span className="font-semibold">{CLAIM[c.decision]}:</span> {c.text}</li>)}</ul>}
    </aside>
  );
};

const Sources = ({ sources }) =>
  sources?.length ? (
    <section className="mt-12" aria-labelledby="src-h" data-testid="article-sources">
      <h2 id="src-h" className="rule-top pt-3 font-display text-xl font-semibold text-ink">Izvori</h2>
      <ol className="mt-4 space-y-3 text-sm">
        {sources.map((s, i) => (
          <li key={s.id} className="flex gap-3" data-testid={`source-item-${i}`}>
            <span className="font-semibold text-muted-foreground">{i + 1}.</span>
            <div>
              <span className="mr-2 rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">{SOURCE_LABELS[s.source_type] || "Izvor"}</span>
              {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="font-medium text-brandblue underline-offset-2 hover:underline dark:text-sky-300">{s.title} <ExternalLink className="inline h-3 w-3" /></a> : <span className="font-medium">{s.title}</span>}
              {s.publisher && <span className="text-muted-foreground"> — {s.publisher}</span>}
            </div>
          </li>
        ))}
      </ol>
    </section>
  ) : null;

function jsonLd(a, url) {
  return {
    "@context": "https://schema.org", "@type": a.article_type === "analiza" ? "AnalysisNewsArticle" : "NewsArticle",
    headline: a.headline, description: a.seo_description || a.subheadline, datePublished: a.published_at, dateModified: a.updated_at,
    mainEntityOfPage: url, image: a.hero_image_url ? [a.hero_image_url.startsWith("http") ? a.hero_image_url : `${SITE_URL}${a.hero_image_url}`] : undefined,
    author: [{ "@type": "Person", name: a.author?.name }], articleSection: a.category?.name, inLanguage: "bs",
    publisher: { "@type": "NewsMediaOrganization", name: "BalkanPuls.de", logo: { "@type": "ImageObject", url: `${SITE_URL}/brand/icon-512.png` } },
  };
}

export default function ArticlePage() {
  const { categorySlug, articleSlug } = useParams();
  const { data: a, loading, error } = useFetch(`/public/articles/${articleSlug}?category=${encodeURIComponent(categorySlug)}`);
  const { reader } = useReader();
  const bodyRef = useRef(null);
  useArticleEngagement(a?.id, bodyRef);
  useEffect(() => {
    if (reader && a?.id) api.post(`/reader/read/${a.id}`, { explicit: false }).catch(() => {});
  }, [reader, a?.id]);
  if (loading) return <BrandLoader />;
  if (error || !a) return <NotFound />;
  if (a.category?.slug && a.category.slug !== categorySlug) return <Navigate to={`/${a.category.slug}/${a.slug}`} replace />;
  const url = `${SITE_URL}/${a.category?.slug || categorySlug}/${a.slug}`;
  const updated = a.updated_at && a.published_at && a.updated_at.slice(0, 16) !== a.published_at.slice(0, 16);
  const sig = (a.corrections || []).filter((c) => c.significant);
  const lastCorr = a.corrections?.length ? a.corrections[a.corrections.length - 1].at : null;

  return (
    <article className="pb-6" data-testid="article-page">
      <Seo title={a.seo_title || a.headline} description={a.seo_description || a.subheadline} image={a.hero_image_url} type="article" jsonLd={jsonLd(a, url)} />
      <div className="container-news max-w-[820px] pt-5">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground" data-testid="breadcrumb">
          <Link to="/" className="hover:underline">Početna</Link> / <Link to={`/${a.category?.slug}`} className="hover:underline" data-testid="breadcrumb-category">{a.category?.name}</Link> / <span className="text-foreground">{ARTICLE_TYPES[a.article_type]}</span>
        </nav>
        {["komentar", "sponzorisano"].includes(a.article_type) && <p className="mt-4 rounded-sm border border-border bg-surface px-3 py-2 text-sm" data-testid="content-type-notice">{a.article_type === "komentar" ? "Ovo je autorski komentar i izražava mišljenje autora, a ne stav redakcije." : `${a.sponsor_label || "Sponzorisani sadržaj"}${a.sponsor_name ? ` – sponzor: ${a.sponsor_name}` : ""}. Nije nezavisno novinarstvo.`}</p>}
        {a.is_demo && <p className="mt-4 rounded-sm bg-amber-100 px-3 py-2 text-sm font-semibold text-amber-950" data-testid="demo-article-banner">DEMO SADRŽAJ – razvojni primjer, nije stvarna vijest.</p>}
        <div className="mt-6 flex items-center gap-2"><span className="kicker">{a.category?.name}</span><span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid="article-type-label">{ARTICLE_TYPES[a.article_type]}</span><DemoBadge show={a.is_demo} /></div>
        <h1 className="hl mt-3 text-[1.6rem] leading-[1.18] sm:text-[2.2rem] lg:text-[2.6rem] lg:leading-[1.12]" data-testid="article-headline">{a.headline}</h1>
        {a.subheadline && <p className="dek mt-4 text-[1.05rem] leading-relaxed text-muted-foreground md:text-[1.2rem]" data-testid="article-subheadline">{a.subheadline}</p>}
        <div className="mt-6 flex flex-col gap-4 border-y border-border py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm" data-testid="article-meta">
            <span className="font-semibold">{a.author?.name}</span>
            <span className="text-muted-foreground"> · Objavljeno <time dateTime={a.published_at}>{fmtDateTime(a.published_at)}</time>{updated && <> · Ažurirano <time dateTime={a.updated_at}>{fmtDateTime(a.updated_at)}</time></>}{lastCorr && <> · <a href="#article-corrections" className="font-semibold text-amber-700 underline" data-testid="article-corrected-date">Ispravljeno <time dateTime={lastCorr}>{fmtDateTime(lastCorr)}</time></a></>} · {a.reading_time} min čitanja</span>
          </div>
          <div className="flex flex-wrap items-center gap-3"><SaveButton id={a.id} /><Share url={url} title={a.headline} id={a.id} /></div>
        </div>
      </div>
      {a.hero_image_url && (
        <figure className="container-news mt-6 max-w-[1080px] px-0 sm:px-6" data-testid="article-hero-image">
          <img src={imgSrc(a.hero_image_url)} alt={a.hero_image_alt || ""} width={1600} height={1067} className="aspect-[3/2] w-full object-cover sm:aspect-[16/9]" fetchpriority="high" />
          {a.image_credit && <figcaption className="mt-2 px-4 text-[12px] text-muted-foreground sm:px-0">Foto: {a.image_credit}</figcaption>}
        </figure>
      )}
      <div className="container-news mt-8 grid max-w-[1080px] gap-12 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="mx-auto w-full max-w-[700px]">
          {sig.length > 0 && (
            <aside className="mb-8 border-2 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100" role="note" data-testid="article-correction-banner">
              <p className="font-bold uppercase tracking-wider">Ispravka</p>
              {sig.map((c) => <p key={c.id} className="mt-1"><time dateTime={c.at}>{fmtDateTime(c.at)}</time>: {c.note}</p>)}
              <p className="mt-2 text-xs">Prvobitno objavljeno {fmtDateTime(a.published_at)}. <Link to="/korrekturen" className="underline">Pravila o ispravkama</Link></p>
            </aside>
          )}
          {a.why_it_matters_html && (
            <aside className="mb-8 rounded-lg border border-border bg-muted/60 p-5" aria-labelledby="wim2-h" data-testid="why-it-matters-box">
              <h2 id="wim2-h" className="text-xs font-bold uppercase tracking-[0.16em] text-pulse">Zašto je ovo važno za tebe?</h2>
              <div className="prose-news mt-2 !text-base" dangerouslySetInnerHTML={{ __html: a.why_it_matters_html }} />
            </aside>
          )}
          <WhatItMeans a={a} />
          <div id="article-body-start" ref={bodyRef} className="prose-news" data-testid="article-body" dangerouslySetInnerHTML={{ __html: a.body_html }} />
          {a.article_type !== "sponzorisano" && <AdSlot slot="in_article" category={a.category?.slug || ""} />}
          {a.show_info_check !== false && <InfoCheck ic={a.info_check} />}
          {a.corrections?.length > 0 && (
            <aside id="article-corrections" className="my-8 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100" data-testid="article-corrections">
              <h2 className="font-semibold">Ispravke</h2>
              <ul className="mt-2 space-y-1">{a.corrections.map((c) => <li key={c.id}><time dateTime={c.at}>{fmtDateTime(c.at)}</time>: {c.note}</li>)}</ul>
            </aside>
          )}
          {a.ai_disclosure?.ai_first_draft ? <p className="text-xs text-muted-foreground" data-testid="ai-disclosure">Napomena o transparentnosti: istraživanje i prvi nacrt ovog teksta pripremljeni su uz pomoć AI alata na osnovu navedenih izvora. Tekst je pregledala{a.ai_disclosure.human_review?.by ? ` i odobrila redakcija (${a.ai_disclosure.human_review.by})` : " redakcija"}{a.ai_disclosure.human_factcheck ? ", a činjenice je provjerio čovjek." : "."}</p>
            : a.ai_assisted && <p className="text-xs text-muted-foreground" data-testid="ai-disclosure">Napomena: pri pripremi ovog teksta korišteni su AI alati. {typeof a.ai_disclosure === "string" && a.ai_disclosure ? a.ai_disclosure : "Tekst je uredio i odobrio čovjek urednik."}</p>}
          <Sources sources={a.sources} />
          {a.tags?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2" data-testid="article-tags">
              {a.tags.map((t) => <span key={t.id} className="rounded-full bg-muted px-3 py-1 text-xs font-medium">#{t.name}</span>)}
            </div>
          )}
          <section className="mt-12 flex gap-4 border-t border-border pt-6" data-testid="author-box">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy font-display text-lg font-bold text-white">{a.author?.name?.[0]}</div>
            <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Autor</p><p className="font-semibold">{a.author?.name}</p>{a.author?.bio && <p className="mt-1 text-sm text-muted-foreground">{a.author.bio}</p>}</div>
          </section>
          <p className="mt-8 text-xs text-muted-foreground">Primijetili ste grešku? <Link to="/kontakt?kategorija=ispravka" className="underline" data-testid="report-correction-link">Prijava greške</Link> · <Link to="/korrekturen" className="underline">Pravila o ispravkama</Link></p>
        </div>
        <div className="hidden lg:block"><div className="sticky top-20 space-y-4" data-testid="article-aside"><p className="kicker">U ovom tekstu</p><p className="text-[13px] text-muted-foreground">{ARTICLE_TYPES[a.article_type]} · {a.reading_time} min čitanja</p>{a.sources?.length > 0 && <a href="#src-h" className="block text-[13px] font-semibold text-ink hover:text-pulse">Izvori ({a.sources.length}) ↓</a>}<Link to="/kontakt?kategorija=ispravka" className="block text-[13px] text-muted-foreground hover:text-pulse">Prijavi grešku</Link></div></div>
      </div>
      {a.article_type !== "sponzorisano" && <div className="container-news"><AdSlot slot="article_sidebar" media="(min-width: 1024px)" category={a.category?.slug || ""} /></div>}
      {a.related?.length > 0 && (
        <section className="container-news mt-16" data-testid="related-articles">
          <SectionHeading title="Povezani tekstovi" to={`/${a.category?.slug}`} id="related" />
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">{a.related.map((r) => <div key={r.id} onClickCapture={() => track("related_article_click", { target_id: r.id }, a.id)}><ArticleCard a={r} /></div>)}</div>
        </section>
      )}
    </article>
  );
}
